//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify";

// Constants
import { getHistoryResultsHeaders } from "../../../constants/tableHeaders";

/**
 * Patient History Dialog Composable
 *
 * Displays patient test history across visits.
 *
 * @param key - Unique key for state isolation
 */
export const usePatientHistoryDialog = (key: string = "default", lisComposable: ReturnType<typeof useLis>) => {
  //
  // Composables
  //

  // Localization
  const { t } = useI18n();

  // Breakpoints
  const { smAndDown } = useDisplay();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  //
  // State
  //

  const isOpen = useState<boolean>(`history.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`history.${key}.loading`, () => false);
  const loadingHistoryId = ref<number | null>(null);
  const selectedTest = useState<any>(`history.${key}.selectedTest`, () => null);
  const historyData = useState<any[]>(`history.${key}.historyData`, () => []);
  const currentVisitId = useState<string | undefined>(`history.${key}.currentVisitId`, () => undefined);

  // In-memory cache: `${barcode}:${mappingKey}` → history array.
  // Keyed per barcode+test so different samples never share stale data.
  // Not persisted to sessionStorage — history is transient and must always
  // reflect live LIS state; the cache only exists to make re-opens feel instant.
  const historyCache = new Map<string, any[]>();

  //
  // Computed Properties
  //

  const headers = computed(() => getHistoryResultsHeaders(t, smAndDown.value));

  const testName = computed(() => {
    return selectedTest.value?.lisName || selectedTest.value?.name || "";
  });

  // Enrich history data with flags
  const enrichedHistory = computed(() => {
    return historyData.value.map((item) => ({
      ...item,
      flags: [],
    }));
  });

  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    headers: headers.value,
    testName: testName.value,
    history: enrichedHistory.value,
    loading: loading.value,
    currentVisitId: currentVisitId.value,
    onClose: close,
  }));

  //
  // Methods
  //

  /**
   * Open dialog and load history.
   *
   * Cache strategy:
   * - Cache hit  → show cached data immediately, open dialog, then silently
   *               background-refresh. Button spinner runs during background fetch
   *               but the dialog never blocks.
   * - Cache miss → block until data arrives (button spinner + no dialog open),
   *               then open. Matches the "no flash" behaviour added previously.
   */
  const open = async (test: any, barcode: string, visitId?: string): Promise<void> => {
    const cacheKey = `${barcode}:${test.mappingKey}`;
    const cached = historyCache.get(cacheKey);

    selectedTest.value = test;
    currentVisitId.value = visitId;
    loadingHistoryId.value = test.id ?? null;

    // ── Cache hit: show immediately, then refresh silently ──────────────────
    if (cached) {
      historyData.value = cached;
      isOpen.value = true;

      try {
        const fresh = await lisComposable.fetchPatientHistory(barcode, test.mappingKey);
        if (fresh?.length) {
          historyCache.set(cacheKey, fresh);
          historyData.value = fresh;
        }
        // If fresh is empty the cached data stays — do not close or snackbar.
      } catch (error) {
        console.error("[usePatientHistoryDialog] Background refresh error:", error);
        // Silent — user already sees the cached data; no snackbar needed.
      } finally {
        loadingHistoryId.value = null;
      }
      return;
    }

    // ── Cache miss: block until we have data ────────────────────────────────
    loading.value = true;

    try {
      const history = await lisComposable.fetchPatientHistory(barcode, test.mappingKey);

      if (history?.length) {
        historyCache.set(cacheKey, history);
        historyData.value = history;
        isOpen.value = true;
      } else {
        showSnackbar(t("pages.validation.dialogs.patientHistory.noHistory"), {
          color: "warning",
          icon: "mdi-history",
        });
      }
    } catch (error) {
      console.error("[usePatientHistoryDialog] Load error:", error);
      showSnackbar(t("components.common.errors.fetchFailed"), {
        color: "error",
        icon: "mdi-alert-circle",
      });
    } finally {
      loading.value = false;
      loadingHistoryId.value = null;
    }
  };

  /**
   * Close dialog and reset state
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — old data stays visible during
    // the close animation. State will be replaced by the next open() call.
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading: readonly(loading),
    loadingHistoryId: readonly(loadingHistoryId),
    selectedTest: readonly(selectedTest),
    historyData: readonly(historyData),
    currentVisitId: readonly(currentVisitId),

    // Computed
    headers: readonly(headers),
    testName: readonly(testName),
    componentProps,

    // Methods
    open,
    close,
  };
};
